/**
 * Delivery zones, redesigned 2026-09-16 as a shipping-settings screen on the template's own pieces:
 *
 * vendor/isomorphic/apps/isomorphic/src/app/shared/account-settings/personal-info.tsx
 *   the settings-screen shape: the editable thing in the main column, what it depends on beside it;
 * vendor/isomorphic/apps/isomorphic/src/app/shared/logistics/tracking/shipping-info.tsx
 *   icon tile + label/value blocks in the "charge settings" card (via `InfoBlock`);
 * vendor/isomorphic/apps/isomorphic/src/app/shared/profile/profile-header.tsx
 *   the hero, through `components/detail-parts.tsx`.
 *
 * The rate card itself — rows, unmatched-pincode quick add, the two-step replace — is
 * `components/orders/delivery-zone-editor.tsx`.
 *
 * Ported from apps/admin/app/(app)/orders/delivery-zones/page.tsx. The rows are data (edited here),
 * and so — FR-048, "configured by an administrator" — are the mode, flat charge, fallback and
 * free-above value, in the "Charge settings" card (`/admin/delivery-settings`). Until someone saves
 * that card, configuration decides, and the card says so. Nothing here evaluates the free-above
 * rule or works out which zone a pincode falls in; `unmatchedPincodes` is the API's own answer.
 * Gated on `config.administer` by `nav.tsx`'s entry through the root loader.
 */
import { useState, type FormEvent } from "react";
import toast from "react-hot-toast";
import { useLoaderData, useRevalidator, useSearchParams } from "react-router-dom";
import {
  PiCheckCircleDuotone as CheckIcon,
  PiCurrencyInrDuotone as RupeeIcon,
  PiMapPinDuotone as MapPinIcon,
  PiSlidersHorizontalDuotone as ModeIcon,
  PiTruckDuotone as TruckIcon,
  PiWarningDuotone as WarningIcon,
} from "react-icons/pi";
import { Alert, Badge, Button, Text } from "rizzui";
import { ErrorSurface, Field, WidgetCard } from "@steelyard/theme";
import { CardTitle, PageHero } from "../../components/detail-parts";
import { DeliveryZoneEditor } from "../../components/orders/delivery-zone-editor";
import { SelectField } from "../../components/select-field";
import {
  getDeliverySettings,
  getDeliveryZones,
  replaceDeliveryZones,
  setDeliverySettings,
  type DeliveryChargeMode,
  type DeliverySettings,
  type DeliveryZonesResult,
} from "../../lib/services/ordering";

export async function deliveryZonesLoader() {
  const [zones, settings] = await Promise.all([getDeliveryZones(), getDeliverySettings()]);
  return { ...(zones as DeliveryZonesResult), settings };
}

const MODE_LABEL: Record<DeliveryChargeMode, string> = {
  flat: "Flat charge on every order",
  pincode_zone: "By pincode zone (the rate card)",
  free_above_value: "Flat charge, free above an order value",
};

/** Which figures a mode reads — the others are kept, not used (the API refuses a mode missing its own). */
const USES: Record<DeliveryChargeMode, { flat: boolean; fallback: boolean; freeAbove: boolean }> = {
  flat: { flat: true, fallback: false, freeAbove: false },
  pincode_zone: { flat: false, fallback: true, freeAbove: false },
  free_above_value: { flat: true, fallback: false, freeAbove: true },
};

const asText = (value: number | string | null | undefined) => (value === null || value === undefined ? "" : String(value));
const asFigure = (value: string) => (value.trim() === "" ? null : Number(value));

/** FR-048: the mode and its figures, edited here; what is in force is re-read from the API after a save. */
function ChargeSettingsCard({ initial }: { initial: DeliverySettings }) {
  const revalidator = useRevalidator();
  const [mode, setMode] = useState(initial.mode as DeliveryChargeMode);
  const [flat, setFlat] = useState(asText(initial.flatCharge));
  const [fallback, setFallback] = useState(asText(initial.fallbackCharge));
  const [freeAbove, setFreeAbove] = useState(asText(initial.freeAboveValue));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const uses = USES[mode] ?? USES.flat;

  async function save(event: FormEvent) {
    event.preventDefault();
    if ([flat, fallback, freeAbove].map(asFigure).some((value) => value !== null && Number.isNaN(value))) {
      toast.error("Enter an amount in rupees, or leave the figure blank");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await setDeliverySettings({
        mode,
        flatCharge: asFigure(flat),
        fallbackCharge: asFigure(fallback),
        freeAboveValue: asFigure(freeAbove),
      });
      toast.success("Delivery charge settings saved");
      revalidator.revalidate();
    } catch (err) {
      setError(err);
      toast.error("Could not save the delivery charge settings");
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={save} className="mt-5 flex flex-col gap-4">
      <Text className="text-sm text-gray-500">
        {initial.source === "database"
          ? "Saved here. Applies to the next checkout; orders already placed keep their charge."
          : "Currently from configuration — saving here takes over. Applies to the next checkout only."}
      </Text>
      {error ? <ErrorSurface error={error} action="save the delivery charge settings" /> : null}
      <SelectField label="Charging mode" name="mode" value={mode} onChange={(event) => setMode(event.target.value as DeliveryChargeMode)}>
        {(Object.keys(MODE_LABEL) as DeliveryChargeMode[]).map((key) => (
          <option key={key} value={key}>
            {MODE_LABEL[key]}
          </option>
        ))}
      </SelectField>
      {uses.flat ? (
        <Field label="Flat charge (₹)" name="flatCharge" inputMode="decimal" value={flat} onChange={(event) => setFlat(event.target.value)} />
      ) : null}
      {uses.freeAbove ? (
        <Field
          label="Free above order value (₹)"
          name="freeAboveValue"
          inputMode="decimal"
          value={freeAbove}
          onChange={(event) => setFreeAbove(event.target.value)}
          hint="A cart at or above this value pays no delivery."
        />
      ) : null}
      {uses.fallback ? (
        <Field
          label="Fallback charge (₹)"
          name="fallbackCharge"
          inputMode="decimal"
          value={fallback}
          onChange={(event) => setFallback(event.target.value)}
          hint="Charged when the shipping pincode matches no zone; that pincode is then reported above."
        />
      ) : null}
      <div>
        <Button type="submit" disabled={busy}>
          {busy ? "Saving…" : "Save charge settings"}
        </Button>
      </div>
    </form>
  );
}

export default function DeliveryZonesPage() {
  const data = useLoaderData() as Awaited<ReturnType<typeof deliveryZonesLoader>>;
  const [searchParams, setSearchParams] = useSearchParams();
  const saved = searchParams.get("saved") === "1";
  const revalidator = useRevalidator();

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);

  async function handleSave(zones: { pincodeFrom: string; pincodeTo: string; charge: number }[]) {
    setBusy(true);
    setError(null);
    try {
      await replaceDeliveryZones(zones);
      toast.success("Delivery zones saved");
      // A second save leaves the URL as it was (`?saved=1`), which would not reload the zones.
      setSearchParams({ saved: "1" });
      revalidator.revalidate();
    } catch (err) {
      setError(err);
      toast.error("Could not save the delivery zones");
    } finally {
      setBusy(false);
    }
  }

  const unmatched = data.unmatchedPincodes.length;

  return (
    <div className="@container flex flex-col gap-6">
      <PageHero
        breadcrumb={[{ name: "Home", href: "/" }, { name: "Orders", href: "/orders" }, { name: "Delivery Zones" }]}
        back={{ href: "/orders", label: "All orders" }}
        icon={<TruckIcon aria-hidden className="size-9" />}
        iconClassName="bg-blue"
        title="Delivery Zones"
        description="Pincode ranges and the delivery charge each one carries. Changes apply to later checkouts only."
        meta={
          <>
            <Badge variant="flat" color="info" rounded="pill" className="gap-1.5">
              <MapPinIcon aria-hidden className="size-4" />
              {data.zones.length === 1 ? "1 zone saved" : `${data.zones.length} zones saved`}
            </Badge>
            {unmatched > 0 ? (
              <Badge variant="flat" color="warning" rounded="pill" className="gap-1.5">
                <WarningIcon aria-hidden className="size-4" />
                {unmatched === 1 ? "1 pincode matches no zone" : `${unmatched} pincodes match no zone`}
              </Badge>
            ) : (
              <Badge variant="flat" color="success" rounded="pill" className="gap-1.5">
                <CheckIcon aria-hidden className="size-4" />
                No unmatched pincodes reported
              </Badge>
            )}
          </>
        }
      />

      {error ? <ErrorSurface error={error} action="save the delivery zones" /> : null}

      {saved ? (
        <div role="status">
          <Alert color="success" variant="flat" bar icon={<CheckIcon aria-hidden className="size-5" />}>
            Saved. The rate card below is now what Infra Junction charges on the next checkout.
          </Alert>
        </div>
      ) : null}

      <div className="grid grid-cols-1 items-start gap-6 @5xl:grid-cols-12">
        <div className="min-w-0 @5xl:col-span-8">
          <DeliveryZoneEditor initialZones={data.zones} unmatchedPincodes={data.unmatchedPincodes} onSave={handleSave} busy={busy} />
        </div>

        <WidgetCard
          rounded="xl"
          className="@5xl:col-span-4"
          title={
            <CardTitle icon={<ModeIcon aria-hidden className="size-5" />} tile="bg-orange-lighter text-orange-dark">
              Charge settings
            </CardTitle>
          }
          action={
            <Badge variant="flat" rounded="pill" className="gap-1.5 whitespace-nowrap">
              <RupeeIcon aria-hidden className="size-3.5" />
              {data.settings.source === "database" ? "Saved here" : "From configuration"}
            </Badge>
          }
          headerClassName="items-center"
        >
          {/* Keyed on what the API returned, so a save's re-read resets the form to the rule in force. */}
          <ChargeSettingsCard key={JSON.stringify(data.settings)} initial={data.settings} />
        </WidgetCard>
      </div>
    </div>
  );
}
